import { useRef, useEffect } from "react";
import "./TextTooltipOnly.css"
// import "./TextWithTooltip.css";

function TextTooltipOnly({ text, className, placement = "top", maxWidth = 260, maxHeight = 160, children }) {
  const targetRef = useRef(null);
  const bsInstanceRef = useRef(null);
  const hideTimerRef = useRef(null);
  const showTimerRef = useRef(null);

  useEffect(() => {
    if (!window.bootstrap?.Tooltip || !targetRef.current) return;

    // Clean previous instance
    if (bsInstanceRef.current) {
      bsInstanceRef.current.dispose();
      bsInstanceRef.current = null;
    }

    const escape = (s) =>
      String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    
    const htmlTitle = `<div class="twt-content" style="--twt-maxw:${maxWidth}px; --twt-maxh:${maxHeight}px;">${escape(
      text ?? ""
    ).replace(/\n/g, "<br>")}</div>`;

    // Create Bootstrap Tooltip instance
    // bsInstanceRef.current = new window.bootstrap.Tooltip(targetRef.current, {
    //   trigger: "manual",
    //   placement,
    //   customClass: "tooltip-twt",
    //   html: true,
    //   title: htmlTitle,
    // });
    bsInstanceRef.current = new window.bootstrap.Tooltip(targetRef.current, {
        trigger: "manual",
        placement,
        customClass: "tooltip-twt-header",
        html: true,
        title: htmlTitle,
        popperConfig: (defaultConfig) => ({
            ...defaultConfig,
            modifiers: [
            ...(defaultConfig.modifiers || []),
            { name: "flip", enabled: false },   // Disable auto-flip
            { name: "preventOverflow", options: { padding: 0 } }, 
            ]
        })
        });
    const inst = bsInstanceRef.current;

    const clearShowTimer = () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const show = () => {
      clearShowTimer();
      clearHideTimer();
      inst.show();

      const tipEl = inst.getTipElement?.() || document.querySelector(".tooltip.show");
      if (tipEl) {
        tipEl.addEventListener("mouseenter", clearHideTimer);
        tipEl.addEventListener("mouseleave", scheduleHide);

        tipEl.addEventListener("wheel", (e) => {
          const el = e.currentTarget.querySelector(".twt-content");
          if (!el) return;
          const atTop = el.scrollTop === 0 && e.deltaY < 0;
          const atBottom =
            el.scrollHeight - el.clientHeight - Math.ceil(el.scrollTop) <= 0 && e.deltaY > 0;
          if (!atTop && !atBottom) e.stopPropagation();
        }, { passive: true });
      }
    };

    const scheduleShow = () => {
      clearShowTimer();
      showTimerRef.current = setTimeout(show, 200); // Hover delay
    };

    const scheduleHide = () => {
      clearShowTimer();
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => inst.hide(), 250);
    };

    const handleEnter = () => scheduleShow();
    const handleLeave = () => scheduleHide();
    const handleFocus = () => show();
    const handleBlur = () => scheduleHide();

    const tgt = targetRef.current;
    tgt.addEventListener("mouseenter", handleEnter);
    tgt.addEventListener("mouseleave", handleLeave);
    tgt.addEventListener("focus", handleFocus);
    tgt.addEventListener("blur", handleBlur);

    return () => {
      clearShowTimer();
      clearHideTimer();
      tgt.removeEventListener("mouseenter", handleEnter);
      tgt.removeEventListener("mouseleave", handleLeave);
      tgt.removeEventListener("focus", handleFocus);
      tgt.removeEventListener("blur", handleBlur);
      if (bsInstanceRef.current) {
        bsInstanceRef.current.dispose();
        bsInstanceRef.current = null;
      }
    };
  }, [text, placement, maxWidth, maxHeight]);

  return (
    <span
      ref={targetRef}
      className={className || ""}
      tabIndex={0}
      aria-label={text || ""}
    >
      {children} {/* Only render the children (e.g., the icon or other component) */}
    </span>
  );
}

export default TextTooltipOnly;
